import React from 'react'

import { ETaskStatus } from 'types/GlobalTask'
import { SxProps } from '@mui/material'

import { useTask } from 'redux/hooks/Tasks'
import { useTaskActions } from 'redux/actions/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import LinearProgress from 'components_new/atoms/LinearProgress'
import Text from 'components_new/atoms/Text'

import ChipSelect from 'components_new/molecules/ChipSelect'
import ContentBox from 'components_new/molecules/ContentBox'
import ValueText from 'components_new/molecules/ValueText'

import SelectUser from 'components_new/organisms/SelectUser'

import { getParsedDate } from 'utils/dateParser'
import Icon from 'components_new/atoms/Icon'
import { Highlighted } from '../../StandardSearch/utils'

interface TaskItemProps {
  displayActions?: boolean
  disableBorder?: boolean
  disableIcon?: boolean
  id: string | null
  search?: string | null
  sx?: SxProps
}

const TaskItem = (props: TaskItemProps) => {
  const { displayActions, disableBorder, disableIcon, id, search, sx } = props

  const { lang, t, translateEnum } = useTranslation()
  const task = useTask(id)
  const { tryUpdateTask } = useTaskActions()

  const handleUpdateStatus = (value: ETaskStatus) => {
    if (!task) return

    tryUpdateTask(task.id, { status: value })
  }

  const handleUpdateUser = (value: string | null) => {
    if (!id) return

    tryUpdateTask(id, {
      responsible_account_id: value
    })
  }

  if (!task) return null

  const nSubtasks = task.sub_tasks.length
  const nDoneSubtasks = task.sub_tasks.filter(
    (item) => item.status === ETaskStatus.DONE
  ).length

  return (
    <>
      <ContentBox
        border={!disableBorder}
        header={{
          actions: (
            <ValueText
              color="success.text"
              displayPositiveIndicator={true}
              unit={'kr/år'}
              value={task.noi_change}
            />
          ),
          icon: disableIcon ? { color: 'success', name: 'Task' } : undefined,
          title: (
            <Highlighted
              highlight={search}
              text={task.title || t('global', 'unnamed')}
            />
          )
        }}
        key={task.id}
        sx={sx}
      >
        {task.sub_tasks.length > 0 ? (
          <>
            <LinearProgress
              customLabel={
                <>
                  {`${nDoneSubtasks}/${nSubtasks}`}{' '}
                  <Icon
                    name="Tasks"
                    sx={{ fontSize: '1em', verticalAlign: 'middle' }}
                  />
                </>
              }
              value={(nDoneSubtasks / nSubtasks) * 100}
              variant="determinate"
              sx={{ mb: 1 }}
            />
          </>
        ) : null}

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label={task.metric_filter_value} size="xsmall" />

            <ChipSelect
              color={translateEnum('task_status', task.status).color}
              key="status"
              onChange={(value) => handleUpdateStatus(value.id as ETaskStatus)}
              options={[
                ETaskStatus.TODO,
                ETaskStatus.IN_PROGRESS,
                ETaskStatus.DONE
              ].map((status) => {
                return {
                  color: translateEnum('task_status', status).color,
                  icon: translateEnum('task_status', status).iconName,
                  id: status,
                  title: translateEnum('task_status', status).title
                }
              })}
              size="xsmall"
              sx={{ cursor: 'default' }}
              value={task.status}
            />
          </Box>

          <SelectUser
            onChange={handleUpdateUser}
            size="xsmall"
            sx={{ cursor: 'default' }}
            value={task.responsible_account_id}
          />
        </Box>

        <Text color="text.secondary" component="div" variant="caption">
          {`${t('global', 'created')} ${getParsedDate(task.created_at, lang, {
            time: 'full'
          })}`}
        </Text>

        {displayActions ? (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            <Button fullWidth={true} href={`/tasks/${task.id}`} size="small">
              {t('global', 'go_to')}
            </Button>
          </Box>
        ) : null}
      </ContentBox>
    </>
  )
}

export default TaskItem
